import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Camera, Plus, Search, ArrowDownUp, UserPlus, Phone, ChevronRight, X } from 'lucide-react';
import { Fab } from '@/components/Fab';
import { useClientsInfinite, useTags } from '@/lib/queries';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Avatar } from '@/components/ui/avatar';
import { ScopeSwitcher } from '@/components/ScopeSwitcher';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { fmtMoney } from '@/lib/format';
import { useQueryClient } from '@tanstack/react-query';

export const ClientsPage = () => {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState('name');
  const [tagId, setTagId] = useState<string>('');
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useClientsInfinite({
    q: q || undefined,
    sort,
    tag_id: tagId || undefined,
  });
  const { data: tags = [] } = useTags();

  const items = useMemo(() => {
    const pages = data?.pages ?? [];
    const seen = new Set<string>();
    const merged: any[] = [];
    for (const page of pages) {
      for (const c of page.items) {
        if (seen.has(c.id)) continue;
        seen.add(c.id);
        merged.push(c);
      }
    }
    return merged;
  }, [data?.pages]);

  const total = data?.pages[0]?.total ?? items.length;

  useEffect(() => {
    const target = loadMoreRef.current;
    if (!target) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { rootMargin: '200px' },
    );
    obs.observe(target);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage, items.length]);
  const totalRevenue = items.reduce((s: number, c: any) => s + Number(c.stats?.total ?? 0), 0);
  const totalDebt = items.reduce((s: number, c: any) => s + Number(c.stats?.debt ?? 0), 0);

  return (
    <div className="animate-in-up space-y-4 pb-fab">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Клиенты</h1>
          <p className="text-xs text-muted-foreground">
            {total > items.length
              ? `${items.length} из ${total} карточек`
              : `${items.length} карточек`}
          </p>
        </div>
        <ScopeSwitcher />
      </div>

      {/* Stats strip */}
      {items.length > 0 && (
        <div className="grid grid-cols-2 gap-2.5">
          <Card>
            <CardContent className="space-y-0.5 p-3">
              <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Всего выручка
              </div>
              <div className="text-lg font-bold tabular">{fmtMoney(totalRevenue)}</div>
            </CardContent>
          </Card>
          <Card className={totalDebt > 0 ? 'border-warning/30 bg-warning-soft/40' : ''}>
            <CardContent className="space-y-0.5 p-3">
              <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                Долги
              </div>
              <div
                className={`text-lg font-bold tabular ${
                  totalDebt > 0 ? 'text-warning' : ''
                }`}
              >
                {fmtMoney(totalDebt)}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          className="pl-11 pr-4"
          placeholder="Имя или телефон…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      <div className="flex gap-2">
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="h-10 flex-1 text-sm">
            <ArrowDownUp className="h-3.5 w-3.5 text-muted-foreground" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name">По имени</SelectItem>
            <SelectItem value="last_visit">По последнему визиту</SelectItem>
            <SelectItem value="total_amount">По сумме</SelectItem>
            <SelectItem value="visits_count">По числу визитов</SelectItem>
          </SelectContent>
        </Select>
        <Select value={tagId || 'all'} onValueChange={(v) => setTagId(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-10 flex-1 text-sm">
            <SelectValue placeholder="Все теги" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Все теги</SelectItem>
            {tags.map((t: any) => (
              <SelectItem key={t.id} value={t.id}>
                {t.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skeleton h-[76px] rounded-2xl" />
          ))}
        </div>
      )}

      <div className="space-y-2">
        {items.map((c: any) => (
          <ClientRow key={c.id} c={c} />
        ))}
        {!isLoading && !items.length && (
          <Card>
            <CardContent className="flex flex-col items-center gap-3 p-10 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary-soft text-primary">
                <UserPlus className="h-6 w-6" />
              </div>
              <div>
                <div className="font-semibold">Нет клиентов</div>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Добавьте первого клиента и запишите его на услугу
                </p>
              </div>
            </CardContent>
          </Card>
        )}
        <div ref={loadMoreRef} className="h-1 w-full" aria-hidden />
        {isFetchingNextPage &&
          Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="skeleton h-[76px] rounded-2xl" />
          ))}
      </div>

      <NewClientButton />
    </div>
  );
};

const ClientRow = ({ c }: { c: any }) => {
  const lastVisit = c.stats?.last_visit_date
    ? new Date(c.stats.last_visit_date)
    : null;

  return (
    <Link to={`/clients/${c.id}`} className="block">
      <Card className="group transition-all hover:-translate-y-0.5 hover:shadow-soft-md active:scale-[0.99]">
        <CardContent className="flex items-center gap-3 p-3">
          <Avatar src={c.photo_url} name={c.name} size="lg" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="truncate font-semibold">{c.name}</span>
              {c.tags?.slice(0, 2).map((t: any) => (
                <span
                  key={t.id}
                  className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold"
                  style={{
                    background: `${t.color}1f`,
                    color: t.color,
                  }}
                >
                  {t.name}
                </span>
              ))}
            </div>
            <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
              {c.phone && (
                <span className="flex items-center gap-1">
                  <Phone className="h-3 w-3" /> {c.phone}
                </span>
              )}
              <span>
                {c.stats?.visits_count ?? 0} виз.
                {lastVisit && ` · ${lastVisit.toLocaleDateString('ru-RU')}`}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 flex-col items-end justify-between gap-1">
            <div className="text-sm font-bold tabular">{fmtMoney(c.stats?.total ?? 0)}</div>
            {c.stats?.debt > 0 ? (
              <Badge variant="warning" className="text-[9px]">
                Долг {fmtMoney(c.stats.debt)}
              </Badge>
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5" />
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

const NewClientButton = () => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [tg, setTg] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const qc = useQueryClient();

  const pickPhoto = (file: File | null) => {
    if (!file) return;
    if (file.type && !file.type.startsWith('image/')) return;
    if (file.size > 10 * 1024 * 1024) return;
    setPhotoFile(file);
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result === 'string') setPhotoPreview(result);
    };
    reader.readAsDataURL(file);
  };
  const clearPhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
  };

  const reset = () => {
    setName('');
    setPhone('');
    setTg('');
    clearPhoto();
  };

  const submit = async () => {
    if (!name.trim() || submitting) return;
    setSubmitting(true);
    try {
      const { data } = await api.post('/clients', {
        name: name.trim(),
        phone: phone || undefined,
        telegram_username: tg || undefined,
      });
      if (photoFile) {
        try {
          const safe =
            photoFile.type && photoFile.type.startsWith('image/')
              ? photoFile
              : new File([photoFile], photoFile.name || 'photo.jpg', {
                  type: 'image/jpeg',
                });
          const fd = new FormData();
          fd.append('file', safe);
          await api.post(`/clients/${data.id}/photos`, fd);
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('Не удалось загрузить фото клиента', err);
        }
      }
      await qc.invalidateQueries({ queryKey: ['clients'], refetchType: 'all' });
      setOpen(false);
      reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) reset();
      }}
    >
      <DialogTrigger asChild>
        <Fab label="Новый клиент" />
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Новый клиент</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="relative flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-dashed border-border bg-muted/40 text-muted-foreground hover:border-primary hover:text-primary"
            >
              {photoPreview ? (
                <img src={photoPreview} alt="" className="h-full w-full object-cover" />
              ) : (
                <Camera className="h-5 w-5" />
              )}
            </button>
            <div className="text-xs text-muted-foreground">
              {photoPreview ? (
                <button
                  type="button"
                  onClick={clearPhoto}
                  className="inline-flex items-center gap-1 text-destructive"
                >
                  <X className="h-3 w-3" /> Убрать фото
                </button>
              ) : (
                'Фото — необязательно'
              )}
            </div>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                pickPhoto(e.target.files?.[0] ?? null);
                e.target.value = '';
              }}
            />
          </div>
          <div>
            <Label>Имя *</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Анна Иванова" />
          </div>
          <div>
            <Label>Телефон</Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              type="tel"
              placeholder="+7 (___) ___-__-__"
            />
          </div>
          <div>
            <Label>Telegram</Label>
            <Input value={tg} onChange={(e) => setTg(e.target.value)} placeholder="@username" />
          </div>
          <Button onClick={submit} disabled={!name.trim() || submitting} className="w-full" size="lg">
            <UserPlus className="h-4 w-4" /> {submitting ? 'Создание…' : 'Создать'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
